import { SHTable } from "@components/design-systems";
import { FeatureAnalysisRowLevel as RowLevel } from "@models/platform-analysis/enums/feature/featureAnalysisRowLevel";
import { styled } from "@mui/material";
import { RootState } from "@redux/store";
import { hexToRGBA } from "@utils";
import { debounce } from "lodash";
import { useEffect } from "react";
import { useSelector } from "react-redux";
import { useToggle } from "react-use";

interface AnalysisVirtualizedTableWrapperProps {
  scrolledLeft?: boolean;
  collapsed?: boolean;
  hideArrow?: boolean;
  showSectionTooltip?: boolean;
  showSubSectionTooltip?: boolean;
  showFeatureTooltip?: boolean;
}

export const AnalysisVirtualizedTableWrapper = styled(SHTable, {
  shouldForwardProp: (prop) =>
    ![
      "scrolledLeft",
      "collapsed",
      "hideArrow",
      "showSectionTooltip",
      "showSubSectionTooltip",
      "showFeatureTooltip",
    ].includes(prop as string),
})<AnalysisVirtualizedTableWrapperProps>(
  ({
    theme: { palette },
    scrolledLeft,
    collapsed,
    hideArrow,
    showSectionTooltip = false,
    showSubSectionTooltip = false,
    showFeatureTooltip = true,
  }) => {
    const [localScrolledLeft, toggleScrolledLeft] = useToggle(!!scrolledLeft);
    const [localCollapsed, toggleCollapsed] = useToggle(!!collapsed);

    const storedIsShowScore = useSelector(
      (state: RootState) => state.platformAnalysis?.ui?.isShowAnalysisScores,
    );

    const debounceUpdateTableStyles = debounce(
      ({
        scrolledLeft,
        shouldCollapse,
      }: {
        scrolledLeft: boolean;
        shouldCollapse: boolean;
      }) => {
        scrolledLeft !== localScrolledLeft && toggleScrolledLeft(scrolledLeft);
        shouldCollapse !== localCollapsed && toggleCollapsed(shouldCollapse);
      },
      100,
    );

    useEffect(() => {
      debounceUpdateTableStyles({
        scrolledLeft: !!scrolledLeft,
        shouldCollapse: !!collapsed,
      });

      return () => debounceUpdateTableStyles.cancel();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scrolledLeft, collapsed]);

    return {
      display: "grid",
      borderCollapse: "separate",
      borderSpacing: 0,
      gap: 8,
      "& .GroupScoreProductCell": {
        opacity: storedIsShowScore ? 1 : 0,
        transition: "opacity 0.3s ease-in-out",
      },
      "& .SubGroupProductScoreCell": {
        opacity: storedIsShowScore ? 1 : 0,
        transition: "opacity 0.3s ease-in-out",
      },
      "& .SubGroupProductScoreCell-image-icon-only": {
        opacity: storedIsShowScore ? 0 : 1,
        height: storedIsShowScore ? 0 : "auto",
        transition: "opacity 0.3s ease-in-out",
      },
      "& .FeatureAnalysisTitleCell-arrow": {
        display: hideArrow ? "none" : "flex",
      },
      "& .FeatureAnalysisTitleSHTableCell": {
        pointerEvents: hideArrow ? "none" : "auto",
        "& .FeatureAnalysis-row-description": {
          pointerEvents: "auto",
        },
      },
      [`& .FeatureAnalysisTitleCell-${RowLevel.Section}`]: {
        "& .FeatureAnalysis-row-description": {
          display: showSectionTooltip ? "block" : "none",
        },
        "& .FeatureAnalysisTitleCell-arrow": {
          paddingLeft: showSectionTooltip ? 4 : 0,
        },
      },
      [`& .FeatureAnalysisTitleCell-${RowLevel.SubSection}`]: {
        "& .FeatureAnalysis-row-description": {
          display: showSubSectionTooltip ? "block" : "none",
        },
        "& .FeatureAnalysisTitleCell-arrow": {
          paddingLeft: showSectionTooltip ? 4 : 0,
        },
      },
      [`& .FeatureAnalysisTitleCell-${RowLevel.Feature}`]: {
        "& .FeatureAnalysis-row-description": {
          display: showFeatureTooltip ? "block" : "none",
        },
      },
      tr: {
        ":last-of-type": {
          "& .FeatureAnalysisTitleCell, .FeatureAnalysisRenderedCell": {
            borderBottom: `1px solid ${palette.secondary[100]}`,
          },
        },
      },
      [`& .FeatureAnalysisTitleCell-${RowLevel.SubSection},
                  .FeatureAnalysisTitleCell-${RowLevel.Feature}`]: {
        backgroundColor: hexToRGBA(
          localScrolledLeft ? palette.common.lighterGrey : palette.common.white,
          1,
        ),
      },
      [`& .FeatureAnalysisRenderedCell-${RowLevel.Section}`]: {
        backgroundColor: palette.common.white,
      },
      [`& .FeatureAnalysisRenderedCell-${RowLevel.SubSection},
                  .FeatureAnalysisRenderedCell-${RowLevel.Feature}`]: {
        backgroundColor: hexToRGBA(palette.common.lighterGrey, 0.5),
      },
      "& .ProductCell-avatar-and-score": {
        flexDirection: "column",
        justifyContent: "center",
        alignItems: localCollapsed ? "flex-start" : "center",
        "& .ProductCell-avatar": {
          flexDirection: localCollapsed ? "row" : "column",
          alignItems: "center",
          gap: localCollapsed ? 12 : 8,
          paddingBottom: 4,
          paddingTop: 12,
        },
        "& .ProductCell-avatar-name": {
          alignItems: localCollapsed ? "start" : "center",
          textAlign: localCollapsed ? "left" : "center",
        },
        "& .ProductCell-avatar-image": {
          width: localCollapsed ? 33 : 50,
          height: localCollapsed ? 33 : 50,
          transition: "width 0.3s ease-in-out, height 0.3s ease-in-out",
        },
        "& .ProductCell-score": {
          flexDirection: localCollapsed ? "row" : "column",
          gap: localCollapsed ? 9.5 : 0,
          "& .ProductCell-score-title": {
            fontSize: localCollapsed ? 11 : 13,
          },
          "& .ProductCell-score-value": {
            fontSize: localCollapsed ? 16 : 20,
            fontWeight: localCollapsed ? 600 : 700,
            transition: "all 0.3s ease-in-out",
          },
        },
      },
      "& .ProductCell-decision-btn": {
        maxHeight: localCollapsed ? 20 : 30,
        marginTop: 4,
      },
      "& .ProductCell-delete-btn, & .ProductCell-restore-btn": {
        marginTop: 4,
        height: localCollapsed ? 20 : 30,
        width: localCollapsed ? 35 : 30,
        transition: "all 300ms ease-in-out",
        borderRadius: localCollapsed ? "25px" : undefined,
        svg: {
          width: localCollapsed ? 14 : 16,
          height: localCollapsed ? 14 : 16,
        },
      },
      "& .DecisionBanner": {
        minHeight: localCollapsed ? 10 : 20,
        maxHeight: localCollapsed ? 10 : 20,
        marginTop: localCollapsed ? 5 : 8,
        transition: "all 300ms ease-in-out",
        "& .DecisionBanner-content": {
          visibility: localCollapsed ? "hidden" : "visible",
        },
        "& .DecisionBanner-content.pseudo": { visibility: "hidden" },
      },
      "& .ProductCell-main-badge": {
        borderColor: `${palette.warning.main} !important`,
        top: 5,
        left: localCollapsed ? 28 : "auto",
        right: localCollapsed ? "auto" : 12,
        transition: "all 0.3s ease",
        height: localCollapsed ? 12 : 22,
        "& >.MuiChip-label": {
          overflow: "unset",
          fontSize: localCollapsed ? 9 : 13,
          paddingTop: 2,
        },
      },
      "& .ProductCell-main-badge.sub-product": {
        left: localCollapsed ? 21 : "auto",
        top: localCollapsed ? 10 : 5,
      },
    };
  },
);
